import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import {
  deleteCadParcelImage,
  getCadParcelPresignedUrl,
  putCadParcelImageToS3,
  saveCadParcelImage,
  setCadParcelCoverImage,
} from '../../services/cadService';
import { toast } from '../../Components/Toast';
import { confirmDialog } from '../../Components/ConfirmDialog';
import ListingImageTile from '../../Map/components/DockPanel/Tabs/ListingImageTile';
import ListingImageCarousel from '../../Map/components/DockPanel/Tabs/ListingImageCarousel';
import ListingImageLightbox from '../../Map/components/DockPanel/Tabs/ListingImageLightbox';
import {
  LISTING_COVER_HINT,
  LISTING_IMAGE_LIMIT_TOAST,
  LISTING_IMAGE_MIN_TOAST,
  LISTING_MAX_IMAGES,
  LISTING_MIN_IMAGES,
  applyCoverImageToListingImages,
  isListingImageLimitError,
  isListingImageMinError,
  normalizeListingImagesFromPayload,
} from '../../Map/utils/listingImageUtils';
import { buildCadParcelPhotosPatch } from '../utils/cadParcelDisplay';

const applyImages = (imagesOrPayload) => {
  if (Array.isArray(imagesOrPayload)) {
    return normalizeListingImagesFromPayload({ photos: imagesOrPayload });
  }
  return normalizeListingImagesFromPayload(imagesOrPayload);
};

const resolveSavedImages = (payload, currentImages) => {
  if (Array.isArray(payload?.photos)) return applyImages(payload);
  const url = payload?.url || payload?.image_url || payload?.imageUrl;
  if (payload?.id || url) {
    return applyImages([...currentImages, { ...payload, url: url || payload?.url }]);
  }
  return currentImages;
};

/**
 * Galería de fotos de un lote CAD. Mismo chrome que UF / contenido de lote.
 * Escritura: JWT + can_manage. Mínimo 0, máximo 12. No viaja en PATCH.
 */
export default function CadParcelImages({
  parcelId,
  canEdit = false,
  initialImages,
  coverImage = null,
  hideEmpty = false,
  showCoverBadge = true,
  onImagesChange,
}) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [settingCoverId, setSettingCoverId] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setImages(applyImages({
      photos: Array.isArray(initialImages) ? initialImages : [],
      cover_image: coverImage ?? null,
    }));
  }, [initialImages, coverImage]);

  const notifyChange = (next, nextCover) => {
    const payload = buildCadParcelPhotosPatch({
      photos: Array.isArray(next) ? next : next?.photos,
      cover_image: nextCover !== undefined
        ? nextCover
        : (next?.cover_image ?? null),
    });
    setImages(payload.photos);
    onImagesChange?.(payload);
  };

  const handleMultipleUpload = async (files) => {
    const fileArray = Array.from(files || []);
    if (fileArray.length === 0 || !parcelId) return;

    const available = LISTING_MAX_IMAGES - images.length;
    if (available <= 0) {
      toast.warning(LISTING_IMAGE_LIMIT_TOAST);
      return;
    }
    if (fileArray.length > available) {
      toast.warning({
        title: 'Límite de fotos',
        message: `Solo podés agregar ${available} foto${available !== 1 ? 's' : ''} más.`,
      });
      return;
    }

    setUploading(true);
    let errors = 0;
    let hitLimit = false;
    let currentImages = [...images];
    try {
      for (const file of fileArray) {
        if (!file.type.startsWith('image/')) {
          errors += 1;
          continue;
        }
        try {
          const presignedRes = await getCadParcelPresignedUrl({
            parcelId,
            filename: file.name,
            contentType: file.type,
          });
          if (!presignedRes.ok || !presignedRes.data?.presignedUrl) {
            if (isListingImageLimitError(presignedRes._httpStatus, presignedRes.message)) {
              hitLimit = true;
              break;
            }
            throw new Error('No se obtuvo la URL de subida.');
          }
          const {
            presignedUrl,
            s3Url,
            image_url: imageUrlFromPresign,
            imageUrl: imageUrlAlt,
          } = presignedRes.data;
          const uploaded = await putCadParcelImageToS3({
            presignedUrl,
            file,
            contentType: file.type,
          });
          if (!uploaded.ok) throw new Error('Error al subir la imagen a S3.');
          const savedImageUrl = s3Url || imageUrlFromPresign || imageUrlAlt;
          if (!savedImageUrl) throw new Error('No se obtuvo la URL de la foto.');
          const saveRes = await saveCadParcelImage({
            parcelId,
            imageUrl: savedImageUrl,
          });
          if (!saveRes.ok || !saveRes.data) {
            if (isListingImageLimitError(saveRes._httpStatus, saveRes.message)) {
              hitLimit = true;
              break;
            }
            throw new Error('Error al guardar la imagen.');
          }
          currentImages = resolveSavedImages(saveRes.data, currentImages);
          notifyChange(
            currentImages,
            saveRes.data?.cover_image !== undefined
              ? saveRes.data.cover_image
              : undefined,
          );
        } catch (e) {
          errors += 1;
          if (e.message === 'limit') {
            hitLimit = true;
            break;
          }
        }
      }
      if (hitLimit) {
        toast.warning(LISTING_IMAGE_LIMIT_TOAST);
      } else if (errors === 0 && fileArray.length > 0) {
        toast.success(`${fileArray.length > 1 ? 'Fotos subidas' : 'Foto subida'} correctamente.`);
      } else if (errors > 0 && errors < fileArray.length) {
        toast.warning({
          title: 'Parcialmente completado',
          message: `${fileArray.length - errors} foto(s) subidas. ${errors} no pudieron subirse.`,
        });
      } else if (errors === fileArray.length) {
        toast.error({ title: 'Error', message: 'No se pudieron subir las fotos.' });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (image) => {
    if (images.length <= LISTING_MIN_IMAGES) {
      toast.warning(LISTING_IMAGE_MIN_TOAST);
      return;
    }

    const result = await confirmDialog({
      title: '¿Eliminar foto?',
      description: 'Se eliminará definitivamente. Esta acción no se puede deshacer.',
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      variant: 'danger',
    });
    if (!result.isConfirmed) return;

    setDeletingIds((prev) => new Set([...prev, image.id]));
    const prevImages = images;
    notifyChange(images.filter((img) => img.id !== image.id));

    try {
      const res = await deleteCadParcelImage({ imageId: image.id });
      if (!res.ok) {
        if (isListingImageMinError(res._httpStatus, res.message)) {
          toast.warning(LISTING_IMAGE_MIN_TOAST);
        } else {
          toast.error({ title: 'Error', message: res.message || 'No se pudo eliminar la foto. Intentá de nuevo.' });
        }
        throw new Error('delete failed');
      }
      notifyChange(
        applyCoverImageToListingImages(
          prevImages.filter((img) => img.id !== image.id),
          res.data?.cover_image ?? null,
        ),
        res.data?.cover_image ?? null,
      );
    } catch (e) {
      if (e.message !== 'delete failed') {
        console.error('Error deleting CAD parcel image:', e);
        toast.error({ title: 'Error', message: 'No se pudo eliminar la foto. Intentá de nuevo.' });
      }
      notifyChange(prevImages);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(image.id);
        return next;
      });
    }
  };

  const handleSetCover = async (image) => {
    if (!image?.id || image.is_cover || images.length <= 1 || !parcelId) return;

    setSettingCoverId(image.id);
    try {
      const res = await setCadParcelCoverImage({ parcelId, imageId: image.id });
      if (!res.ok) {
        toast.error({
          title: 'Error',
          message: res.message || 'No se pudo cambiar la portada.',
        });
        return;
      }
      notifyChange(
        applyCoverImageToListingImages(images, res.data?.cover_image ?? image),
        res.data?.cover_image ?? image,
      );
      toast.success('Portada actualizada.');
    } catch (e) {
      console.error('Error setting CAD parcel cover:', e);
      toast.error({ title: 'Error', message: 'No se pudo cambiar la portada.' });
    } finally {
      setSettingCoverId(null);
    }
  };

  if (hideEmpty && !canEdit && images.length === 0) return null;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
        {images.length > 0 ? (
          canEdit ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {images.map((image) => (
                <ListingImageTile
                  key={image.id}
                  image={image}
                  canEdit={canEdit}
                  isDeleting={deletingIds.has(image.id)}
                  isSettingCover={settingCoverId === image.id}
                  showSetCover={canEdit && images.length > 1 && !image.is_cover}
                  showCoverBadge={showCoverBadge}
                  onOpen={(opened) => {
                    const next = images.findIndex((item) => item.id === opened.id);
                    setLightboxIndex(next >= 0 ? next : 0);
                  }}
                  onDelete={handleDelete}
                  onSetCover={handleSetCover}
                />
              ))}
            </Box>
          ) : (
            <ListingImageCarousel images={images} />
          )
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 2.5,
              borderRadius: 1.5,
              border: '1px dashed rgba(255,255,255,0.12)',
              bgcolor: 'rgba(255,255,255,0.02)',
            }}
          >
            <PhotoOutlinedIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.28)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>
              Sin fotos
            </Typography>
          </Box>
        )}

        {canEdit && images.length < LISTING_MAX_IMAGES && (
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleMultipleUpload(e.target.files)}
            />
            <Box
              onClick={() => !uploading && fileInputRef.current?.click()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                p: 1.5,
                border: '1px dashed rgba(255,255,255,0.15)',
                borderRadius: 1.5,
                cursor: uploading ? 'default' : 'pointer',
                color: 'rgba(255,255,255,0.4)',
                fontSize: '12px',
                fontFamily: 'inherit',
                transition: 'all 0.15s ease',
                '&:hover': uploading ? {} : {
                  borderColor: 'rgba(255,255,255,0.30)',
                  color: 'rgba(255,255,255,0.7)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                },
              }}
            >
              {uploading ? (
                <>
                  <CircularProgress size={14} sx={{ color: 'rgba(255,255,255,0.4)' }} />
                  <Typography variant="caption" sx={{ color: 'inherit', fontFamily: 'inherit' }}>
                    Subiendo...
                  </Typography>
                </>
              ) : (
                <>
                  <AddPhotoAlternateIcon sx={{ fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: 'inherit', fontFamily: 'inherit' }}>
                    Agregar foto ({images.length}/{LISTING_MAX_IMAGES})
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}

        {canEdit && images.length >= LISTING_MAX_IMAGES && (
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.30)',
              fontSize: '11px',
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Límite de {LISTING_MAX_IMAGES} fotos alcanzado
          </Typography>
        )}

        {canEdit && images.length > 1 && (
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.28)',
              fontSize: '10px',
              textAlign: 'center',
            }}
          >
            {LISTING_COVER_HINT}
          </Typography>
        )}
      </Box>

      {canEdit && lightboxIndex != null ? (
        <ListingImageLightbox
          images={images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </>
  );
}
